import type { Id } from "@repo/convex/dataModel";
import type { Editor } from "@tiptap/react";
import { Square, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import type { AnalyticsSurface } from "@/lib/analytics/events";
import { cn } from "@/lib/utils";
import { useVoiceComposer, type VoiceComposerSource } from "../hooks/use-voice-composer";
import { formatRecordingTime } from "../hooks/use-voice-recorder";
import { VoiceTriggerButton } from "./voice-trigger-button";

interface VoiceInputButtonProps {
  editor: Editor | null;
  projectId?: Id<"projects"> | null;
  threadId?: string | null;
  source: VoiceComposerSource;
  surface?: AnalyticsSurface;
  disabled?: boolean;
  compact?: boolean;
  snapshotSelectionOnStart?: boolean;
  lockEditorWhileRecording?: boolean;
  onTranscriptionCreated?: (transcriptionId: Id<"voiceTranscriptions">) => void;
}

function RecordingLevelBars({ level }: { level: number }) {
  const bars = [
    ["low-left", 0.25],
    ["mid-left", 0.45],
    ["center", 0.7],
    ["mid-right", 0.5],
    ["low-right", 0.35],
  ] as const;
  return (
    <span className="flex h-4 items-center gap-0.5" aria-hidden="true">
      {bars.map(([key, base]) => (
        <span
          key={key}
          className="w-0.5 rounded-full bg-primary transition-[height,opacity]"
          style={{
            height: `${Math.max(4, Math.round((base + level * 0.8) * 14))}px`,
            opacity: 0.4 + Math.min(0.6, level),
          }}
        />
      ))}
    </span>
  );
}

/**
 * Legacy inline voice control: a mic button that morphs into a compact recording pill. New composer
 * and document-toolbar surfaces render their own recording strips directly while sharing
 * `useVoiceComposer` for recorder/transcription logic.
 */
export function VoiceInputButton({
  editor,
  projectId,
  threadId,
  source,
  surface,
  disabled = false,
  compact = false,
  snapshotSelectionOnStart = false,
  lockEditorWhileRecording = false,
  onTranscriptionCreated,
}: VoiceInputButtonProps) {
  const voice = useVoiceComposer({
    editor,
    projectId,
    threadId,
    source,
    surface,
    disabled,
    snapshotSelectionOnStart,
    lockEditorWhileRecording,
    onTranscriptionCreated,
  });

  if (voice.isRecording) {
    return (
      <output
        className={cn(
          "flex h-8 items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-2 text-xs text-foreground",
          compact && "h-6 px-1.5"
        )}
        aria-label={voice.isPaused ? "Recording paused for silence" : "Recording"}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className={cn("size-6 rounded-full", compact && "size-5")}
          onClick={voice.discard}
          aria-label="Cancel voice input"
        >
          <X className="size-3.5" />
        </Button>
        <RecordingLevelBars level={voice.level} />
        <span className="tabular-nums text-[11px] text-muted-foreground">
          {formatRecordingTime(voice.elapsedMs)}
        </span>
        {voice.isPaused ? <span className="text-[11px] text-muted-foreground">Paused</span> : null}
        <Button
          type="button"
          variant="default"
          size="icon-sm"
          className={cn("size-6 rounded-full", compact && "size-5")}
          onClick={() => void voice.accept()}
          disabled={voice.isStopping}
          aria-label="Stop and transcribe"
        >
          {voice.isStopping ? (
            <Spinner className="size-3.5" />
          ) : (
            <Square className="size-3 fill-current" />
          )}
        </Button>
      </output>
    );
  }

  return <VoiceTriggerButton voice={voice} className={cn(compact && "!size-4 !p-0")} />;
}
